/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 4 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { untranslated, useTranslation } from "@i18n";
import React, { useEffect, useRef, useState, forwardRef, Suspense, lazy, useImperativeHandle } from "react";
import axiosWrapper from "@Services/axiosWrapper";
import { useAbortableApi } from "@Services/useAbortableApi";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import {
  getScheduleActivitiesUrl,
  saveScheduleActivityUrl,
  deleteScheduleActivityUrl,
} from "../../../services/dataManager";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { ObjectId } from "bson";
import Shimmer from "../../Queries/Shimmer";
import "./dhtmlxgantt.css";
import "./dhtmlxganttCustom.css";
import "./dhtmlxgantt.js";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);
import { Menu, MenuItem, Fade, FormControlLabel, CircularProgress } from "@mui/material";
import UpdatesModal from "./UpdatesModal";
import GridToolbar from "@CubeComponents/html/SlickGrid1/ToolBar";
import { useGanttViews, isPrestigeProd, isPrestigeOnlyColumn, isToggleableGanttColumn } from "./useGanttViews";
import ReactDOM from "react-dom/client";
import ActivityInformationModal from "./ActivityInformationModal";
import ganttHelper from "./useGanttHelper";
import useContracts from "./useContracts";
import useToolBar, { applyParentTypeUpdates, type GanttUpdateItem } from "./useToolbar";
import { getBoqItems } from "../../../actions/schedules";
import { getSelectedSelectionSetItemsList } from "@Actions/selection";
import { useResizeDetector } from "react-resize-detector";
import { setScheduleDetails, setSelectionSetTaskElements } from "@Actions/task";
import Simulation from "./Simulation";
import { setResourceCreated } from "@Reducers/Resources";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import NoDataSvg from "@Assets/icons/svg/NoData.svg?raw";
import DownArrow from "@Assets/icons/svg/BottomArrow.svg?react";
import ColumnChooserIcon from "@Assets/icons/svg/columnChooser.svg?react";
import FullScreenIcon from "@Assets/icons/svg/Full_Screen.svg?react";
import MinimiseIcon from "@Assets/icons/svg/Minimise.svg?react";
import FilterButton, { type FilterConditionEntry } from "@CubeComponents/html/SlickGrid1/Modals/FilterButton";
import { useGanttFilters } from "./useGanttFilters";
import { setRenderHeader } from "../../../reducers/portalSetting";
import { setShowStickySidebar } from "@Reducers/stickySidebar";
import Text from "@CubeComponents/html/Text/Text";
import { GANTT_VIEW_TYPE, GanttProps, GanttQueueItem, GanttViewType } from "../interface";
import type { GetSelectionSetItemsResponse } from "@ServerTypes/selectionSet/selectionSet.schema";

/**
 * `getSelectionSetDataAndQueriesData` returns both lists as opaque record arrays; these are the
 * fields this component actually reads off them.
 */
type SelectionSetSummary = { _id?: unknown; itemsVersionNo?: number; query?: string };

/** One item of a selection set, as `getSelectedSelectionSetItemsList` returns it. */
type SelectionSetItem = NonNullable<GetSelectionSetItemsResponse["data"]>[number];

/**
 * One link embedded in a schedule activity, exactly as the server forwards it
 * (`taskLinks: task.taskLinks || []` in schedule.controller.ts — no normalisation). Mirrors the
 * stored `taskLink` shape in `packages/server/tasks/tasks.collection.ts`, where `type` is nullable
 * on legacy / P6-imported links; that is what the `?? "0"` below guards against. The narrower
 * `processedTaskLinkSchema` on the response declares `type` non-null and does not match those rows.
 */
type StoredTaskLink = { id: string; source: string; target: string; type: string | null; lag?: number };
import { showToastNotification } from "@Reducers/toastSlice";
import { GLOBAL, SIMULATION, SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import {
  folderPermissions,
  GANTT_SCHEDULE_RESOURCE_TYPE,
  GANTT_SCHEDULE_SELECTED_LAYOUT_KEY,
} from "@Helpers/constants";
import { applyFilter } from "@Features/viewer-modals/utils/searchOperators";
import {
  AttachedItems,
  ColumnData,
  ComponentRefs,
  DependencyData,
  InlineEditorCell,
  RootState,
  WarningState,
  CustomObject,
  InlineEditorMethods,
  Link,
  ToolbarItem,
  DateTimeEditorState,
  GanttLink,
  GanttTask,
  ProcessedTaskLinkFrontend,
  Task,
  Worktime,
  ResourceAssignment,
  TaskOwner,
} from "@WebTypes/scheduleDomain";
import type { GanttStatic, GridColumn as GanttGridColumn, WorkDaysTuple } from "./dhtmlxgantt";
const ColumnChooser = lazy(() => import("./ColumnChooser"));
declare const gantt: GanttStatic;
import _ from "lodash";
import type { GetScheduleActivitiesResponse } from "@ServerTypes/schedule/schedule.schema";
import type {
  GetResourcesForScheduleResponse,
  GetWorktimesForScheduleResponse,
  BatchUpdateProgressResponse,
  UpdateTasksDateNewResponse,
  GetSelectionSetDataAndQueriesDataResponse,
} from "@ServerTypes/tasks/tasks.schema";
import { resolveFileVersionIds } from "@Services/utils/resolveFileVersionIds";
import { getModelPropertiesData } from "@Services/utils/modelPropertiesDataDB";
import { runWithConcurrency } from "@Services/utils/helpers";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";

function isInlineEditorCell(value: unknown): value is InlineEditorCell {
  return typeof value === "object" && value !== null && "id" in value && "columnName" in value;
}

const summaryCalculatedColumns = ["progress", "actualStartDate", "actualEndDate", "plannedStartDate", "plannedEndDate"];

const GanttChart = forwardRef<ComponentRefs, GanttProps>((props, ref) => {
  const { ref: resizeRef, width } = useResizeDetector();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const url = useParams<Record<string, string>>();
  const api = useAbortableApi();
  const {
    changeOrderState,
    scheduleId,
    scheduleVersionId,
    spaceId,
    projectId,
    contractDetails,
    contractId,
    attachTaskModalState,
    setDisableTabs,
    showFullScreen,
    setShowFullScreen,
    fetchVersions,
  } = props;
  const [showWarning, setShowWarning] = useState<WarningState>({ status: false });
  const taskCounter = useRef(1);
  const [showBoqModal, setShowBoqModal] = useState<boolean>(false);
  const [currentSelectedTask, setCurrentSelectedTask] = useState({});
  const [showActivityInformationModal, setShowActivityInformationModal] = useState<boolean>(false);
  const [rerenderGantt, setRerenderGantt] = useState<boolean | null>(null);
  const [enableImportButton, setEnableImportButton] = useState<boolean>(true);
  const [showTaskModal, setShowTaskModal] = useState<boolean>(false);
  const [loadingActivity, setLoadingActivity] = useState<boolean>(false);
  const [attachedItems, setAttachedItems] = useState<AttachedItems>({ items: null });
  const [showCriticalPath, setShowCriticalPath] = useState(false);
  const taskIdAndSuccessorMapper = useRef<Record<string, DependencyData[]>>({});
  const taskIdAndPredecessorMapper = useRef<Record<string, DependencyData[]>>({});
  const taskIdAndTaskDataMapper = useRef<Record<string, GanttTask>>({});
  const isAutoScheduling = useRef(false);
  const isDeletingTask = useRef(false);
  const dateUpdateInProgress = useRef(false);
  const isDragging = useRef(false);
  const ganttConfig = useRef("day");
  const [showShimmer, setShowShimmer] = useState(true);
  const [showResourceLoadDiagram, setShowResourceLoadDiagram] = useState<boolean>(false);
  const [showProgressModal, setShowProgressModal] = useState<boolean>(false);
  const ganttFilterConditionsRef = useRef<Record<string, FilterConditionEntry>>({});
  const isGroupedRef = useRef<boolean>(false);
  const [isGrouped, setIsGrouped] = useState<boolean>(false);
  const [layoutAnchorEl, setLayoutAnchorEl] = useState<HTMLElement | null>(null);
  const [inlineEditingEnabled, setInlineEditingEnabled] = useState<boolean>(false);
  const [showColumnChooserModal, setShowColumnChooserModal] = useState<boolean>(false);
  const [allColumns, setAllColumns] = useState<ColumnData[]>([]);
  const [savedColumns, setSavedColumns] = useState<ColumnData[]>([]);
  const [showDeleteActivityModal, setShowDeleteActivityModal] = useState<boolean>(false);
  const [loadingProgressModal, setLoadingProgressModal] = useState<boolean>(false);
  const [ganttDataAvailable, setGanttDataAvailable] = useState<boolean>(false);
  const layoutOpen = Boolean(layoutAnchorEl);
  const datetimeEditorState: DateTimeEditorState = {
    root: null,
    value: null,
  };
  /** dhtmlx link-type codes, indexed by the numeric code: "0"=FS, "1"=SS, "2"=FF, "3"=SF. */
  const DEPENDENCY_TYPE_BY_CODE = ["FS", "SS", "FF", "SF"];
  /**
   * Legacy / imported links store `type` as null (see `taskLink` in tasks.collection.ts) and dhtmlx
   * treats an unknown link type as finish-to-start, so out-of-range codes resolve to "FS" rather
   * than leaving the dependency row's type blank.
   */
  const getDependencyType = (linkType: string | number | null | undefined): string =>
    DEPENDENCY_TYPE_BY_CODE[Number(linkType)] ?? "FS";
  let idParentBeforeDeleteTask: string = "0";
  let idParentBeforeDragTask: string = "0";
  const skipAfterTaskUpdate = useRef(false);
  const ganttTaskQueue: GanttQueueItem[] = [];
  let ganttQueueRunning = false;
  const resourceCreated = useSelector((state: RootState) => state.resourceCreated);
  const filesData = useSelector((state: RootState) => state.filesData);
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const GanttDetails = useSelector((state: RootState) => state.GanttDetails);
  const { RESOURCE_MODIFY } = folderPermissions();
  const canEdit = GanttDetails.userPermissions?.includes(RESOURCE_MODIFY.id);

  const processTasksAndLinks = (tasks: GanttTask[]): { taskArray: GanttTask[]; taskLinksArray: GanttLink[] } => {
    const taskLinksArray: GanttLink[] = [];

    const taskArray: GanttTask[] = tasks.map((task) => {
      const ganttTask: GanttTask = {
        ...task,
        start_date: task.start_date ? new Date(task.start_date) : undefined,
        end_date: task.end_date ? new Date(task.end_date) : undefined,
        actualStartDate: task.actualStartDate ? new Date(task.actualStartDate) : null,
        actualEndDate: task.actualEndDate ? new Date(task.actualEndDate) : null,
        plannedStartDate: task.plannedStartDate ? new Date(task.plannedStartDate) : null,
        plannedEndDate: task.plannedEndDate ? new Date(task.plannedEndDate) : null,
      };
      taskIdAndTaskDataMapper.current[ganttTask.id.toString()] = ganttTask;

      const taskLinks = task.taskLinks;
      if (Array.isArray(taskLinks)) {
        taskLinks.forEach((link: StoredTaskLink) => {
          if (link.source && link.target) {
            taskLinksArray.push({
              id: link.id,
              source: link.source,
              target: link.target,
              type: link.type ?? "0",
              lag: link.lag ?? 0,
            });
          }
        });
      }

      return ganttTask;
    });

    return { taskArray, taskLinksArray };
  };

  const fetchGanttTasksData = async () => {
    const response = await api.post<GetScheduleActivitiesResponse>(getScheduleActivitiesUrl, {
      params: { projectId, scheduleId, spaceId },
      value: {
        scheduleId,
        scheduleVersionId,
        projectId,
        spaceId,
        contractId,
        contractVersionId: _.get(contractDetails, "versionId"),
        fileVersionId: _.get(url, "fileId"),
      },
    });
    const backendData = response.data;
    let tasks = backendData.result?.taskArray;

    // KNOWN BUG: these are wire rows (`scheduleActivityRowSchema`), not `GanttTask`s — that is what
    // `processTasksAndLinks` converts them INTO. On the wire every date is epoch millis, `duration`/
    // `progress`/`remDuration`/`totalSlack` are `number | string | null`, `critical` is
    // `boolean | string`, `parent`/`calendar_id`/`TaskDisp` are nullable and `id` is optional, while
    // `GanttTask` declares `Date`, plain numbers, non-null and a required `id`. The assertion states
    // the conversion has already happened, so none of that is checked — including the dead
    // `task.end_date` read below (neither producer emits that key: the schedule branch sends no end
    // date at all and the contract branch sends `endDate`, schedule.controller.ts:109).
    // Fixing it needs `GanttTask` (src/types/scheduleDomain/types.ts) to state the wire unions it
    // is built from, or a declared row→task normaliser; both live outside this file.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see KNOWN BUG above
    const { taskArray, taskLinksArray } = processTasksAndLinks((tasks || []) as GanttTask[]);

    const result = {
      result: {
        taskArray,
        taskLinksArray,
        resources: _.get(backendData, "result.resources", []),
        worktimes: _.get(backendData, "result.worktimes", []),
        scheduleData: _.get(backendData, "result.scheduleData", {}),
      },
    };

    taskIdAndTaskDataMapper.current = {};
    taskIdAndSuccessorMapper.current = {};
    taskIdAndPredecessorMapper.current = {};

    const scheduleDataForMapper = {
      data: result.result.scheduleData,
    };

    if (result.result.taskLinksArray.length > 0) {
      updateDependencyMapper(result.result.taskLinksArray, scheduleDataForMapper);
    }

    return result;
  };

  const {
    ganttLayout,
    ganttWithResourceLayout,
    ganttConfigs,
    ganttColumns,
    ganttTemplates,
    header_height,
    getHoursPerDay,
    calculateProgressAndPlannedDateForParentTasks,
    refreshParentSummaries,
  } = ganttHelper(ganttConfig, _.get(userDetails, "timeZone"), contractId, scheduleVersionId, isGroupedRef);

  useImperativeHandle(ref, () => ({
    refreshGanttData: () => {
      setRerenderGantt(true);
    },
    openColumnChooser: () => {
      handleColumnChooserClick();
    },
  }));

  const { linkBoqItemsModal, warningModal, showTaskContractsModal, getTaskGridInContract } = useContracts({
    gantt,
    showBoqModal,
    setShowBoqModal,
    setRerenderGantt,
    contractDetails: contractDetails!,
    showWarning,
    setShowWarning,
    contractId,
    setTaskModal: setShowTaskModal,
    spaceId,
    scheduleId,
    projectId,
    changeOrderState,
    attachedItems,
    setAttachedItems,
  });

  const { ganttToolbar, toolbarClickHandler, deleteActivityModal, importModal, updateModal } = useToolBar({
    contractId,
    scheduleId,
    projectId,
    spaceId,
    enableImportButton,
    currentSelectedTask,
    setShowActivityInformationModal,
    inlineEditingEnabled,
    // Required by the toolbar; the chart is never mounted without them. Erased assertion.
    contractDetails: contractDetails!,
    changeOrderState,
    setShowProgressModal,
    setCurrentSelectedTask,
    setShowShimmer,
    setShowWarning,
    setShowBoqModal,
    // Required by the toolbar; the chart is never mounted without them. Erased assertions.
    attachTaskModalState: attachTaskModalState!,
    taskIdAndPredecessorMapper,
    ganttConfig,
    setRerenderGantt,
    showDeleteActivityModal,
    setShowDeleteActivityModal,
    timezone: userDetails.timeZone,
    scheduleVersionId,
    refreshParentSummaries,
    saveBatchSummaryUpdates,
    setSkipAfterTaskUpdate: (value: boolean) => {
      skipAfterTaskUpdate.current = value;
    },
    isDeletingTask,
    isGrouped,
  });

  const safeResetLayout = () => {
    if (isDragging.current) {
      return;
    }
    gantt.resetLayout();
  };

  useEffect(() => {
    safeResetLayout();
  }, [width]);

  useEffect(() => {
    if (resourceCreated) {
      setRerenderGantt(true);
      dispatch(setResourceCreated(false));
    }
  }, [resourceCreated]);

  useEffect(() => {
    if (showResourceLoadDiagram) {
      gantt.config.layout = ganttWithResourceLayout;
    } else {
      gantt.config.layout = ganttLayout;
    }
    safeResetLayout();
  }, [showResourceLoadDiagram]);

  const handleCriticalPath = () => {
    gantt.config.highlight_critical_path = !showCriticalPath;
    setShowCriticalPath(!showCriticalPath);
    gantt.refreshData();
  };

  const updateDependencyMapper = (
    data: ProcessedTaskLinkFrontend[],
    scheduleData: { data: { config?: { duration_unit?: string } } },
  ) => {
    data.forEach((link: ProcessedTaskLinkFrontend) => {
      const predecessorData = {
        id: link.id.toString(),
        TaskDisp: taskIdAndTaskDataMapper.current[link.source]?.TaskDisp || "",
        name: taskIdAndTaskDataMapper.current[link.source]?.text || "",
        type: getDependencyType(link.type),
        lag:
          scheduleData.data.config?.duration_unit === "hour"
            ? Math.ceil((link.lag ?? 0) / getHoursPerDay(taskIdAndTaskDataMapper.current[link.source]))
            : (link.lag ?? 0),
        predId: link.source,
      };

      const successorData = {
        id: link.id.toString(),
        TaskDisp: taskIdAndTaskDataMapper.current[link.target]?.TaskDisp || "",
        name: taskIdAndTaskDataMapper.current[link.target]?.text || "",
        type: getDependencyType(link.type),
        lag:
          scheduleData.data.config?.duration_unit === "hour"
            ? Math.ceil((link.lag ?? 0) / getHoursPerDay(taskIdAndTaskDataMapper.current[link.target]))
            : (link.lag ?? 0),
        successorId: link.target,
      };

      const predecessors = taskIdAndPredecessorMapper.current[link.target];
      if (predecessors) {
        predecessors.push(predecessorData);
      } else {
        taskIdAndPredecessorMapper.current[link.target] = [predecessorData];
      }

      const successors = taskIdAndSuccessorMapper.current[link.source];
      if (successors) {
        successors.push(successorData);
      } else {
        taskIdAndSuccessorMapper.current[link.source] = [successorData];
      }
    });
  };

  const editHelper: {
    init: (inlineEditors: InlineEditorMethods, grid: unknown) => void;
    onShow: (inlineEditors: InlineEditorMethods, node: HTMLElement, grid: unknown) => void;
    onHide: (inlineEditors: InlineEditorMethods, node: HTMLElement, grid: unknown) => void;
    destroy: () => void;
  } = {
    init(inlineEditors, _grid) {
      inlineEditors.attachEvent("onBeforeEditStart", (state) => {
        if (isGroupedRef.current) {
          return false;
        }
        if (
          summaryCalculatedColumns.includes(state.columnName) &&
          gantt.isTaskExists(state.id) &&
          gantt.hasChild(state.id)
        ) {
          return false;
        }
        return true;
      });

      gantt.attachEvent("onTaskDblClick", (id: string, e: MouseEvent) => {
        // `locateCell` walks up from the clicked node; a click that did not land on an element
        // (or landed outside the grid) simply has no cell, which `isInlineEditorCell` rejects.
        const rawCell = e.target instanceof HTMLElement ? inlineEditors.locateCell(e.target) : null;

        if (
          !props.scheduleVersionId &&
          canEdit &&
          !isGroupedRef.current &&
          isInlineEditorCell(rawCell) &&
          inlineEditors.getEditorConfig(rawCell.columnName)
        ) {
          inlineEditors.startEdit(rawCell.id, rawCell.columnName);
          return false;
        }

        return true;
      });

      gantt.attachEvent("onEmptyClick", () => {
        inlineEditors.hide();
        return true;
      });
    },

    onShow(inlineEditors, node, _grid) {
      node.onkeydown = function (e: KeyboardEvent) {
        if (e.defaultPrevented) {
          return;
        }

        let shouldPrevent = true;
        switch (e.key) {
          case "Enter":
            inlineEditors.save();
            break;

          case "Escape":
            inlineEditors.hide();
            break;

          case "Tab":
            if (e.shiftKey) {
              inlineEditors.editPrevCell(true);
            } else {
              inlineEditors.editNextCell(true);
            }
            break;

          default:
            shouldPrevent = false;
            break;
        }

        if (shouldPrevent) {
          e.preventDefault();
        }
      };
    },

    onHide() {},

    destroy() {},
  };

  async function saveBatchSummaryUpdates(
    updates: Array<{
      id: string;
      progress?: number;
      plannedStartDate?: Date | null;
      plannedEndDate?: Date | null;
      plannedDuration?: number;
      actualStartDate?: Date | null;
      actualEndDate?: Date | null;
      type?: string;
    }>,
  ) {
    if (updates.length === 0) return;
    const tasksPayload = updates.map((update) => {
      const payload: Record<string, unknown> = { id: update.id };
      if (update.progress !== undefined) payload.progress = update.progress;
      if (update.plannedStartDate !== undefined) {
        payload.plannedStartDate = update.plannedStartDate ? new Date(update.plannedStartDate).getTime() : null;
      }
      if (update.plannedEndDate !== undefined) {
        payload.plannedEndDate = update.plannedEndDate ? new Date(update.plannedEndDate).getTime() : null;
      }
      if (update.plannedDuration !== undefined) payload.plannedDuration = update.plannedDuration;
      if (update.actualStartDate !== undefined) {
        payload.actualStartDate = update.actualStartDate ? new Date(update.actualStartDate).getTime() : null;
      }
      if (update.actualEndDate !== undefined) {
        payload.actualEndDate = update.actualEndDate ? new Date(update.actualEndDate).getTime() : null;
      }
      if (update.type) payload.type = update.type;
      return payload;
    });
    const result = await axiosWrapper.post<BatchUpdateProgressResponse>(
      `${getBackendUrl()}/schedule/batchUpdateProgress`,
      {
        tasks: tasksPayload,
        scheduleId,
      },
    );
    if (!result.data.status) {
      dispatch(
        showToastNotification({
          content: untranslated(result.data.errorMessage || SOMETHING_WENT_WRONG),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  }

  const loadGanttData = async () => {
    gantt.clearAll();
    gantt.plugins({
      marker: true,
      overlay: true,
      auto_scheduling: true,
      critical_path: true,
      export_api: true,
      tooltip: true,
      grouping: true,
    });

    Object.assign(gantt.config, ganttConfigs);
    Object.assign(gantt.templates, ganttTemplates);
    gantt.config.resource_property = "owner";
    gantt.config.resource_store = "resource";
    gantt.config.process_resource_assignments = true;
    gantt.config.show_empty_state = true;
    gantt.config.editor_types.custom_datetime = {
      show: function (id: string | number, column: GanttGridColumn, config: unknown, placeholder: HTMLElement) {
        placeholder.innerHTML = "";

        if (placeholder.parentNode) {
          placeholder.style.display = "flex";
          placeholder.style.alignItems = "center";
          placeholder.style.justifyContent = "start";
        }
        if (!datetimeEditorState.root) {
          datetimeEditorState.root = ReactDOM.createRoot(placeholder);
        }

        const task = gantt.getTask(id);
        const columnName = column.name?.toString();
        const rawValue = columnName ? _.get(task, columnName) : undefined;
        const value = rawValue ? dayjs(rawValue) : null;
        datetimeEditorState.value = value;

        const handleChange = (newValue: Dayjs | null) => {
          datetimeEditorState.value = newValue;
        };

        return datetimeEditorState.root.render(
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center" }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                timezone={_.get(userDetails, "timeZone")}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: {
                      "& .MuiInputBase-root": {
                        height: "2rem",
                        fontSize: "0.875rem",
                        backgroundColor: "white",
                      },
                      "& input": {
                        padding: "0.375rem 0.25rem",
                      },
                    },
                  },
                }}
                format="DD/MM/YYYY hh:mm A"
                value={value}
                onChange={handleChange}
                autoFocus
              />
            </LocalizationProvider>
          </div>,
        );
      },

      hide: function () {
        if (datetimeEditorState.root) {
          datetimeEditorState.root.unmount();
          datetimeEditorState.root = null;
        }
      },

      set_value: function (value: Date | null) {
        datetimeEditorState.value = value ? dayjs(value) : null;
      },

      get_value: function () {
        return datetimeEditorState.value ? datetimeEditorState.value.toDate() : null;
      },

      is_changed: function (originalValue: Date | null) {
        const current = datetimeEditorState.value;
        if (!originalValue || !current) return true;
        return current.valueOf() !== new Date(originalValue).getTime();
      },

      is_valid: function () {
        return true;
      },

      focus: function () {},
    };
    gantt.config.layout = ganttLayout;
    gantt.config.columns = ganttColumns;

    const [tasksResponse, resourcesResponse, worktimesResponse] = await Promise.all([
      fetchGanttTasksData(),
      api.post<GetResourcesForScheduleResponse>(`${getBackendUrl()}/schedule/getResourcesForSchedule`, {
        scheduleId,
        scheduleVersionId,
      }),
      api.post<GetWorktimesForScheduleResponse>(`${getBackendUrl()}/schedule/getWorktimesForSchedule`, {
        scheduleId,
        scheduleVersionId,
      }),
    ]);

    const ganttData = tasksResponse;
    const resourcesData = _.get(resourcesResponse, "data");
    const worktimesData = _.get(worktimesResponse, "data");

    if (_.get(url, "fileId")) {
      getSelectionSetDataAndQueriesData(_.get(ganttData, "result.taskArray"));
    }
    dispatch(setScheduleDetails({ ...GanttDetails, worktimes: _.get(worktimesData, "data", []) as Worktime[] }));

    if (_.get(GanttDetails, "config.duration_unit") === "hour") {
      gantt.config.duration_unit = "hour";
      ganttConfig.current = "hour";
    }

    if (_.get(ganttData, "result.taskArray.length")) {
      setEnableImportButton(false);
      const tasks = _.get(ganttData, "result.taskArray");

      if (tasks.length > 0) {
        const lastTask = tasks[tasks.length - 1];

        if (lastTask?.TaskID != null) {
          taskCounter.current = lastTask.TaskID + 1;
        }
      }
    } else {
      const today = new Date();
      const startDate = gantt.date.week_start(new Date(today));
      const endDate = gantt.date.add(startDate, 3, "week");
      gantt.config.start_date = startDate;
      gantt.config.end_date = endDate;
    }

    const ganttContainer = document.getElementById("ganttRoot");

    if (ganttContainer && gantt.$root !== ganttContainer) {
      gantt.init("ganttRoot");
      ganttContainer.addEventListener("click", function (event: MouseEvent) {
        // SVG icons inside a row are `Element` but not `HTMLElement`, so widen rather than narrow;
        // a click with no element target has no row to resolve.
        const target = event.target instanceof Element ? event.target : null;

        if (!target) {
          return;
        }

        if (target.closest(".gantt-task-info")) {
          const taskInfoElement = target.closest(".gantt-task-info");
          const taskId = taskInfoElement?.getAttribute("data-task-id");
          if (taskId) {
            const task = gantt.getTask(taskId);
            showTaskItems(task);
            gantt.unselectTask();
            setCurrentSelectedTask({});
          }
          event.stopPropagation();
        } else {
          const taskRow = target.closest(".gantt_row,.gantt_task_content");
          if (!taskRow) {
            gantt.unselectTask();
            setCurrentSelectedTask({});
          }
        }
      });
    }
    handleCalendars((worktimesData.data || []) as Worktime[]);
    gantt.parse({
      data: _.get(ganttData, "result.taskArray", []),
      links: _.get(ganttData, "result.taskLinksArray", []),
      resources: _.get(resourcesData, "data", []),
    });

    calculateProgressAndPlannedDateForParentTasks();

    ganttExtensions();
    ganttAttachEvents();
    gantt.ext.tooltips.tooltipFor({
      selector: "[resourceTooltipValue]",
      // DHTMLX types the tooltip callback as returning `string | number | void`; a missing attribute
      // yields `null`, which it treats the same as "no tooltip".
      html: (_e: Event, node: HTMLElement) => node.getAttribute("resourceTooltipValue") ?? undefined,
    });
    gantt.ext.inlineEditors.setMapping(editHelper);

    if (_.get(gantt, "_dataProcessor")) {
      _.get(gantt, "_dataProcessor").destructor();
      gantt._dataProcessor = null;
    }

    gantt._dataProcessor = gantt.createDataProcessor(async function (
      entity: string,
      action: string,
      data: Task | Link | ResourceAssignment | CustomObject,
    ) {
      if (action === "delete" && entity === "task") {
        const updates = refreshParentSummaries(idParentBeforeDeleteTask);
        if (updates.length > 0) {
          await saveBatchSummaryUpdates(updates);
        }
      }
      if (isAutoScheduling.current || isDeletingTask.current) return false;

      data.start_date = _.get(data, "start_date") ? new Date(_.get(data, "start_date")).getTime() : null;
      data.end_date = _.get(data, "end_date") ? new Date(_.get(data, "end_date")).getTime() : null;
      data.actualStartDate = _.get(data, "actualStartDate") ? new Date(_.get(data, "actualStartDate")).getTime() : null;
      data.actualEndDate = _.get(data, "actualEndDate") ? new Date(_.get(data, "actualEndDate")).getTime() : null;
      data.plannedStartDate = _.get(data, "plannedStartDate")
        ? new Date(_.get(data, "plannedStartDate")).getTime()
        : null;
      data.plannedEndDate = _.get(data, "plannedEndDate") ? new Date(_.get(data, "plannedEndDate")).getTime() : null;
      if (_.get(data, "cost") != null) data.cost = Number(_.get(data, "cost"));
      if (_.get(data, "actualCost") != null) data.actualCost = Number(_.get(data, "actualCost"));

      if (action === "create" || action === "update") {
        if (!_.get(data, "taskUniqueId")) {
          data.taskUniqueId = _.get(data, "id");
        }
      }

      if (entity === "task" && !skipAfterTaskUpdate.current && gantt.isTaskExists(data.id)) {
        const updates: GanttUpdateItem[] = [...refreshParentSummaries(data.id)];
        if (idParentBeforeDragTask && idParentBeforeDragTask !== "0") {
          const newParent = gantt.getParent(data.id);
          if (String(idParentBeforeDragTask) !== String(newParent)) {
            updates.push(...refreshParentSummaries(idParentBeforeDragTask));
            applyParentTypeUpdates(newParent, idParentBeforeDragTask, updates);
          }
          idParentBeforeDragTask = "0";
        }
        if (updates.length > 0) {
          await saveBatchSummaryUpdates(updates);
        }
      }

      delete data.owner;
      ganttTaskQueue.push({
        entity,
        action,
        data: structuredClone(data),
      });
      if (!ganttQueueRunning) {
        ganttQueueRunning = true;
        await processGanttQueue();
      }
      return true;
    });

    setGanttDataAvailable(true);
    setRerenderGantt(false);
  };

  async function processGanttQueue() {
    setLoadingActivity(true);
    let actionName = "";
    while (ganttTaskQueue.length > 0) {
      const batch = ganttTaskQueue.splice(0, 10);
      await runWithConcurrency(
        batch.map((item: GanttQueueItem) => async () => {
          const { entity, action, data } = item;
          actionName = action;
          const scheduleActivityUrl = action === "delete" ? deleteScheduleActivityUrl : saveScheduleActivityUrl;
          const result = await axiosWrapper.post<GetScheduleActivitiesResponse>(scheduleActivityUrl, {
            params: { projectId, scheduleId, spaceId },
            entity,
            action,
            data,
          });

          if (result.data.status) {
            const task = gantt.isTaskExists(data.id) ? gantt.getTask(data.id) : undefined;
            if (action === "create" && "isNewTask" in data && data.isNewTask) {
              taskCounter.current += 1;
              if (task) {
                task.isNewTask = false;
                gantt.refreshTask(data.id);
              }
              dispatch(
                showToastNotification({
                  content: t("Activity Saved Successfully."),
                  type: TOAST_TYPE_SUCCESS,
                }),
              );
              setCurrentSelectedTask({});
            } else if (action === "update") {
              dispatch(
                showToastNotification({
                  content: t("Activity Edited Successfully."),
                  type: TOAST_TYPE_SUCCESS,
                }),
              );
            }
          } else {
            setLoadingActivity(false);
            setShowActivityInformationModal(false);
            dispatch(
              showToastNotification({
                content: untranslated(result.data.errorMessage || SOMETHING_WENT_WRONG),
                type: TOAST_TYPE_ERROR,
              }),
            );
          }
        }),
        10,
      );
    }
    ganttQueueRunning = false;
    await onAllApiCallsDone(actionName);
  }

  async function onAllApiCallsDone(action?: string) {
    setShowDeleteActivityModal(false);
    setLoadingProgressModal(false);
    setLoadingActivity(false);
    setShowActivityInformationModal(false);
    setShowProgressModal(false);
    if (action === "delete") {
      gantt.unselectTask();
      dispatch(
        showToastNotification({
          content: t("Activity Deleted Successfully."),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
    if (fetchVersions) {
      fetchVersions();
    }
    if (gantt.getTaskCount() === 0) {
      setEnableImportButton(true);
    }
  }

  const ganttAttachEvents = () => {
    gantt.attachEvent("onColumnResizeStart", function () {
      isDragging.current = true;
      return true;
    });
    gantt.attachEvent("onColumnResizeEnd", function () {
      isDragging.current = false;
      return true;
    });

    gantt.attachEvent("onRowDragStart", function () {
      if (scheduleVersionId || !canEdit || isGroupedRef.current) {
        return false;
      }
      isDragging.current = true;
      return true;
    });
    gantt.attachEvent("onRowDragEnd", function () {
      isDragging.current = false;
      return true;
    });

    gantt.attachEvent("onBeforeTaskDrag", function () {
      if (scheduleVersionId || !canEdit) {
        return false;
      }
      isDragging.current = true;
      return true;
    });
    gantt.attachEvent("onAfterTaskDrag", function () {
      isDragging.current = false;
      return true;
    });

    gantt.attachEvent("onBeforeTaskMove", function (id: string | number) {
      if (!idParentBeforeDragTask || idParentBeforeDragTask === "0") {
        // DHTMLX returns the root id (a number) for top-level tasks; the id is only ever compared
        // and forwarded as a string.
        idParentBeforeDragTask = String(gantt.getParent(id));
      }
      return true;
    });

    gantt.attachEvent("onBeforeLinkAdd", function () {
      if (scheduleVersionId || !canEdit) {
        return false;
      }
      return true;
    });

    gantt.attachEvent("onBeforeLinkDelete", function () {
      if (scheduleVersionId || !canEdit) {
        return false;
      }
      return true;
    });

    gantt.attachEvent("onBeforeTaskDelete", (id: string | number): boolean => {
      idParentBeforeDeleteTask = String(gantt.getParent(id));
      return true;
    });

    gantt.attachEvent("onBeforeTaskSelected", function (id: string | number) {
      const selectedTask: GanttTask = gantt.getTask(id);
      let actualDuration: number | null = null;
      if (selectedTask.actualStartDate && selectedTask.actualEndDate) {
        actualDuration = gantt.calculateDuration({
          start_date: new Date(selectedTask.actualStartDate),
          end_date: new Date(selectedTask.actualEndDate),
          task: selectedTask,
        });
      }
      setCurrentSelectedTask({
        id: _.get(selectedTask, "id"),
        TaskDisp: _.get(selectedTask, "TaskDisp", ""),
        taskUniqueId: _.get(selectedTask, "taskUniqueId"),
        text: _.get(selectedTask, "text"),
        start_date: selectedTask.start_date && new Date(selectedTask.start_date),
        end_date: selectedTask.end_date && new Date(selectedTask.end_date),
        actualStartDate: selectedTask.actualStartDate ? new Date(selectedTask.actualStartDate) : null,
        actualEndDate: selectedTask.actualEndDate ? new Date(selectedTask.actualEndDate) : null,
        plannedStartDate: selectedTask.plannedStartDate ? new Date(selectedTask.plannedStartDate) : null,
        plannedEndDate: selectedTask.plannedEndDate ? new Date(selectedTask.plannedEndDate) : null,
        progress: selectedTask.progress ? selectedTask.progress * 100 : 0,
        duration: selectedTask.duration,
        type: selectedTask.type,
        predecessor: taskIdAndPredecessorMapper.current[selectedTask.id],
        successor: taskIdAndSuccessorMapper.current[selectedTask.id],
        actualDuration: actualDuration,
        owner: selectedTask.owner,
        taskTypeId: selectedTask.taskTypeId,
        cost: selectedTask.cost,
        actualCost: selectedTask.actualCost,
        calendar_id: selectedTask.calendar_id,
      });
      return true;
    });

    gantt.attachEvent("onLinkCreated", function (link: GanttLink) {
      link.id = new ObjectId().toString();
      return true;
    });

    gantt.attachEvent("onAfterAutoSchedule", async function () {
      if (dateUpdateInProgress.current) {
        return false;
      }
      dateUpdateInProgress.current = true;
      gantt.config.auto_scheduling = false;
      gantt.config.auto_scheduling_strict = false;
      gantt.config.auto_scheduling_compatibility = false;

      const allTasks = gantt.getTaskByTime();

      const updatedTasks = allTasks.map((task: GanttTask) => {
        return {
          id: task.id,
          startDate: task.start_date ? task.start_date.getTime() : null,
          endDate: task.end_date ? task.end_date.getTime() : null,
          duration: task.duration || 0,
          critical: String(gantt.isCriticalTask(task)),
          totalSlack: task.type === "project" && !gantt.hasChild(task.id) ? "0" : String(gantt.getTotalSlack(task)),
        };
      });

      const response = await axiosWrapper.post<UpdateTasksDateNewResponse>(
        `${getBackendUrl()}/schedule/updateTasksDateNew`,
        {
          tasks: updatedTasks,
          scheduleId,
          contractId: contractId,
          contractVersionId: _.get(contractDetails, "versionId"),
        },
      );
      if (_.get(response, "status")) {
        isAutoScheduling.current = false;
        dateUpdateInProgress.current = false;
        setRerenderGantt(true);
      }
    });

    gantt.attachEvent("onBeforeAutoSchedule", (): boolean => {
      isAutoScheduling.current = true;
      return true;
    });
  };

  const ganttExtensions = () => {
    gantt.ext.export_api._sendImportAjaxMSP = async function (config: {
      server?: string;
      store?: number;
      data: FormData;
      callback: (info: unknown) => void;
      durationUnit?: string;
      projectProperties?: unknown;
      taskProperties?: unknown;
      type?: string;
    }) {
      const url = config.server || gantt.ext.export_api._apiUrl;
      const store = config.store || 0;
      const formData = config.data;
      const callback = config.callback;

      const settings = {
        durationUnit: _.get(config, "durationUnit"),
        projectProperties: _.get(config, "projectProperties"),
        taskProperties: _.get(config, "taskProperties"),
      };

      formData.append("type", _.get(config, "type", "msproject-parse"));
      formData.append("data", JSON.stringify(settings));

      if (store) {
        formData.append("store", store.toString());
      }

      const tokenRes = await axiosWrapper.get<{ csrfToken: string | null }>(`${getBackendUrl()}/csrf-token`);
      const csrfToken = tokenRes.data.csrfToken ?? null;
      const xhr = new XMLHttpRequest();
      xhr.withCredentials = true;
      xhr.onreadystatechange = function () {
        if (xhr.readyState === 4 && xhr.status === 0) {
          // network error
          callback(null);
        }
      };

      xhr.onload = function () {
        const fail = xhr.status > 400;
        let info = null;

        if (!fail) {
          try {
            info = JSON.parse(xhr.responseText);
          } catch (e) {
            return Promise.reject(e);
          }
        }

        callback(info);
      };

      xhr.open("POST", url, true);
      xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
      if (csrfToken) {
        xhr.setRequestHeader("X-CSRF-Token", csrfToken);
      }
      xhr.send(formData);
    };

    gantt.ext.inlineEditors.attachEvent("onBeforeEditStart", function () {
      setInlineEditingEnabled(true);
      return true;
    });

    gantt.ext.inlineEditors.attachEvent("onEditEnd", function () {
      setInlineEditingEnabled(false);
    });

    gantt.ext.inlineEditors.attachEvent(
      "onSave",
      function (state: { id: string | number; columnName: string; newValue: number }) {
        let { id, columnName, newValue } = state;
        if (columnName == "progress") {
          const task = gantt.getTask(id);
          task.progress = newValue / 100;
          gantt.updateTask(id);
          return false;
        }
      },
    );

    gantt.ext.emptyStateElement.renderContent = function (container: HTMLElement) {
      const placeholderTextElement = `<div style="font-size: 1.5rem; font-weight: 600; color: #667085;">
      ${t("No Activity Created")}
    </div>
  `;
      const placeholderImageElement = NoDataSvg;

      const placeholderContainer = `<div class="gantt_empty_state"
         style="
           display: flex;
           flex-direction: column;
           align-items: center;
           justify-content: center;
           text-align: center;
           padding: ${header_height / 16}rem;
         ">${placeholderImageElement}${placeholderTextElement}</div>`;
      container.innerHTML = placeholderContainer;
    };
  };

  const handleCalendars = (worktimes: Worktime[]) => {
    const calendars = gantt.getCalendars();
    for (const existingCalendar of calendars) {
      gantt.deleteCalendar(_.get(existingCalendar, "id"));
    }
    for (const calendar of worktimes) {
      if (!_.get(calendar, "dates")) continue;
      // DHTMLX wants exactly seven day flags (Sunday first), so build the tuple rather than a
      // `number[]` that only claims to have seven entries.
      const isWorkingDay = (day: number) => (_.get(calendar, `dates[${day}]`) ? 1 : 0);
      const workingWeek: WorkDaysTuple = [
        isWorkingDay(0),
        isWorkingDay(1),
        isWorkingDay(2),
        isWorkingDay(3),
        isWorkingDay(4),
        isWorkingDay(5),
        isWorkingDay(6),
      ];
      const addedCalendarId = gantt.addCalendar({
        id: _.get(calendar, "isDefault") ? GLOBAL : _.get(calendar, "id"),
        worktime: {
          hours: _.get(calendar, "hours"),
          days: workingWeek,
        },
      });
      const addedCalendar = gantt.getCalendar(addedCalendarId);
      for (const key of Object.keys(_.get(calendar, "dates", {}))) {
        if (+key > 6) {
          const date = new Date(+key);
          addedCalendar.setWorkTime({
            date,
            hours: false,
          });
        }
      }
      gantt.render();
    }
  };

  const showTaskItems = async (task: { id: string | number; originalTaskId?: string | number }) => {
    // Entry point: DHTMLX row ids are `string | number` in the vendored typings, so the Gantt
    // task's id has to be validated here — `getBoqItems` documents `originalTaskId` as 24-hex, and
    // a row without one (schedule tasks not linked to a contract) simply omits it.
    const originalTaskId = objectIdString.safeParse(task.originalTaskId?.toString()).data;
    // `GanttProps.contractDetails` is still an untyped `any`; the value passed in is a
    // `ContractDetail`, whose `versionId` is an `objectIdString`.
    const contractVersionId: ObjectIdString = contractDetails?.versionId!;
    const res = await getBoqItems({
      originalTaskId,
      contractId: contractId!,
      contractVersionId,
    });
    if (res.data.status) {
      setAttachedItems({
        items: res.data.data ?? null,
        contractHasTaskId: task.id.toString(),
        originalTaskId: task.originalTaskId?.toString(),
      });
    }
  };

  useEffect(() => {
    if (!isAutoScheduling.current && ganttDataAvailable) {
      setShowShimmer(false);
    }
  }, [ganttDataAvailable]);

  const refreshGanttContract = _.get(props, "refreshGanttContract");
  useEffect(() => {
    if (refreshGanttContract) {
      setRerenderGantt(true);
      props.setRefreshGanttContract!(false);
    }
  }, [refreshGanttContract]);

  const getSelectionSetDataAndQueriesData = async (taskArray: GanttTask[]) => {
    // Per-task id → the BIM query / selection-set ids assigned through its resource owners.
    let tasksWithQueriesIds: Record<string, ObjectIdString[]> = {};
    let tasksWithSelectionSetIds: Record<string, ObjectIdString[]> = {};
    let queriesIds: ObjectIdString[] = [];
    let selectionSetIds: ObjectIdString[] = [];

    taskArray.forEach((task: GanttTask) => {
      // SHAPE GAP: neither the chart's task type nor `TaskOwner` declares the BIM associations the
      // scheduler writes onto resource assignments (`queriesIds`, and `selectionSetIds` in its string
      // form). Erased assertions.
      const owner = _.get(task, "owner") as TaskOwner[] | undefined;
      // DHTMLX row ids are `string | number`; these maps are keyed by the string form.
      const taskKey = String(task.id);
      if (owner && owner.length) {
        owner.forEach((ownerItem: TaskOwner) => {
          const queriesIdsArr = _.get(ownerItem, "queriesIds") as ObjectIdString[] | undefined;
          if (queriesIdsArr && queriesIdsArr.length) {
            (tasksWithQueriesIds[taskKey] ??= []).push(...queriesIdsArr);
            queriesIds.push(...queriesIdsArr);
          }

          const selectionSetIdsArr = _.get(ownerItem, "selectionSetIds") as ObjectIdString[] | undefined;
          if (selectionSetIdsArr && selectionSetIdsArr.length) {
            (tasksWithSelectionSetIds[taskKey] ??= []).push(...selectionSetIdsArr);
            selectionSetIds.push(...selectionSetIdsArr);
          }
        });
      }
    });

    const uniqueSelectionSetIds = _.uniq(selectionSetIds);

    if (!queriesIds.length && !uniqueSelectionSetIds.length) return;

    const response = await api.post<GetSelectionSetDataAndQueriesDataResponse>(
      `${getBackendUrl()}/schedule/getSelectionSetDataAndQueriesData`,
      {
        queriesIds,
        selectionSetIds: uniqueSelectionSetIds,
      },
    );

    if (_.get(response, "data.status")) {
      const itemsVersionNoBySetId: Record<string, number | undefined> = {};
      response.data.data?.selectionSetData?.forEach((set: SelectionSetSummary) => {
        itemsVersionNoBySetId[String(set._id)] = set.itemsVersionNo;
      });

      const itemsPerSet = await Promise.all(
        uniqueSelectionSetIds.map((id: ObjectIdString) =>
          getSelectedSelectionSetItemsList({ selectionSetId: id }, itemsVersionNoBySetId[id]),
        ),
      );

      const itemsBySelectionSetId: Record<string, SelectionSetItem[]> = {};
      uniqueSelectionSetIds.forEach((id: string, index: number) => {
        itemsBySelectionSetId[id] = itemsPerSet[index] ?? [];
      });

      // Task id → the element ids (`fileId_cubeUniqueId`) the simulation highlights for that task.
      const result: Record<string, string[]> = {};
      // `fileVersionId -> nodeId -> merged node`; keep the store's own row type so the flatten
      // below is a plain `Object.values`, not a re-declaration of what IndexedDB holds.
      let modelPropertiesData: Awaited<ReturnType<typeof getModelPropertiesData>> = {};
      const currentFileVersonId = _.get(url, "fileId");
      if (Object.keys(tasksWithQueriesIds).length && currentFileVersonId) {
        const fileVersionIds = resolveFileVersionIds(filesData, currentFileVersonId);
        modelPropertiesData = await getModelPropertiesData({
          fileVersionId: fileVersionIds,
          originalFileVersionId: currentFileVersonId,
        });
      }

      // Flatten the merged model properties ONCE, then run each task's queries
      // against that flat list — avoids re-flattening the whole tree per
      // (task × query) inside getQueryResult, and skips its convertToDotNotation
      // pass (only fileId/cubeUniqueId are needed here).
      const flatModelProperties = Object.values(modelPropertiesData).flatMap((fileNodes) => Object.values(fileNodes));

      Object.entries(tasksWithQueriesIds).forEach(([_id, queriesIds]) => {
        const ids: string[] = [];
        queriesIds.map((queryId: ObjectIdString) => {
          const queryData = response.data.data?.queriesData?.find((item: SelectionSetSummary) => item._id === queryId);
          if (queryData) {
            const { filteredData } = applyFilter(flatModelProperties, JSON.parse(queryData.query));
            filteredData.forEach((node) => {
              ids.push(`${String(node.fileId)}_${String(node.cubeUniqueId)}`);
            });
          }
        });
        result[_id] = ids;
      });

      Object.entries(tasksWithSelectionSetIds).forEach(([_id, selectionSetIds]) => {
        const elementIds: string[] = [];
        selectionSetIds.map((selectionSetId: ObjectIdString) => {
          const items = itemsBySelectionSetId[selectionSetId];
          if (items?.length) {
            const allElementIds = items.map((data: SelectionSetItem) => data.elementId);
            elementIds.push(...allElementIds);
          }
        });
        if (result[_id]) {
          result[_id].push(...elementIds);
        } else {
          result[_id] = elementIds;
        }
      });

      dispatch(setSelectionSetTaskElements(result));
    }
  };

  useEffect(() => {
    if (rerenderGantt || rerenderGantt === null) {
      setGanttDataAvailable(false);
      setCurrentSelectedTask({});
      setShowShimmer(true);
      loadGanttData();
    } else {
      safeResetLayout();
    }
  }, [rerenderGantt, contractDetails?.versionId]);

  const handleLayoutClick = (event: React.MouseEvent<HTMLElement>) => {
    setLayoutAnchorEl(event.currentTarget);
  };

  const handleLayoutClose = () => {
    setLayoutAnchorEl(null);
  };

  const handleScreenClick = () => {
    if (showFullScreen) {
      dispatch(setRenderHeader(true));
      dispatch(setShowStickySidebar(true));
    } else {
      dispatch(setRenderHeader(false));
      dispatch(setShowStickySidebar(false));
    }
    setShowFullScreen!(!showFullScreen);
  };

  const handleColumnChooserClick = () => {
    const columnsData = gantt.config.columns
      .map((column: GanttGridColumn) => {
        const columnName = _.get(column, "name");
        if (!isToggleableGanttColumn(columnName)) return null;
        if (isPrestigeOnlyColumn(columnName) && !isPrestigeProd()) return null;
        // DHTMLX types a column's `name` as `string | number` and its `label` as `any`; the chooser
        // rows are string-keyed, and a column with no label falls back to its name (same rule as
        // `useGanttViews`). `isToggleableGanttColumn` has already rejected a nameless column.
        return {
          id: String(columnName),
          label: String(column.label ?? columnName),
          isChecked: !column.hide,
        };
      })
      .filter((col: ColumnData | null): col is ColumnData => col !== null);

    const selectedColumns = gantt.config.columns
      .map((column: GanttGridColumn) => {
        const columnName = _.get(column, "name");
        if (!isToggleableGanttColumn(columnName) || _.get(column, "hide")) return null;
        return {
          id: String(columnName),
          label: String(column.label ?? columnName),
          isChecked: !column.hide,
        };
      })
      .filter((col: ColumnData | null): col is ColumnData => col !== null);

    setAllColumns(columnsData);
    setSavedColumns(selectedColumns);
    setShowColumnChooserModal(true);
  };

  const applyColumnVisibility = (selectedColumns: ColumnData[]) => {
    const selectedColumnsName = new Set(selectedColumns.map((column: ColumnData) => _.get(column, "id")));

    for (const column of gantt.config.columns) {
      const columnName = _.get(column, "name");
      if (isToggleableGanttColumn(columnName)) {
        column.hide = !selectedColumnsName.has(String(columnName));
      }
    }

    gantt.render();
    setSavedColumns(selectedColumns);
  };

  const handleColumnChooserSaveClick = (selectedColumns: ColumnData[]) => {
    applyColumnVisibility(selectedColumns);
    notifyViewMutation();
  };

  const plannedAgainstActualColumns = [
    "TaskDisp",
    "text",
    "plannedStartDate",
    "plannedEndDate",
    "plannedDuration",
    "actualStartDate",
    "actualEndDate",
    "actualDuration",
  ];
  const SimulationDefaultColumnsByViewType: Record<GanttViewType, string[]> = {
    [GANTT_VIEW_TYPE.PLANNED]: ["TaskDisp", "text", "plannedStartDate", "plannedEndDate", "plannedDuration"],
    [GANTT_VIEW_TYPE.ACTUAL]: ["TaskDisp", "text", "actualStartDate", "actualEndDate", "actualDuration"],
    [GANTT_VIEW_TYPE.FORECAST]: ["TaskDisp", "text", "start_date", "end_date", "duration"],
    [GANTT_VIEW_TYPE.PLANNED_AGAINST_ACTUAL]: plannedAgainstActualColumns,
    [GANTT_VIEW_TYPE.PLANNED_AGAINST_ACTUAL_PERCENTAGE]: plannedAgainstActualColumns,
  };

  const applySimulationDefaultColumns = (simulationViewType: GanttViewType) => {
    const defaultNames = SimulationDefaultColumnsByViewType[simulationViewType];

    const defaults = gantt.config.columns
      .filter((col) => defaultNames.includes(String(_.get(col, "name"))))
      .map((col) => ({
        id: String(_.get(col, "name")),
        label: _.get(col, "label"),
        isChecked: true,
      }));

    applyColumnVisibility(defaults);
  };

  useEffect(() => {
    // `viewType` is only set while the Simulation tab is mounted; without one there is no default
    // column set to apply.
    if (props.activeTab !== SIMULATION || !ganttDataAvailable || !props.viewType) return;
    applySimulationDefaultColumns(props.viewType);
  }, [props.activeTab, props.viewType, ganttDataAvailable]);

  const {
    ganttFilterColumns,
    ganttTasksDataset,
    handleGanttFilterChange: applyGanttFilters,
    getGanttTaskFieldValue,
  } = useGanttFilters({
    ganttDataAvailable,
    savedColumns,
    rerenderGantt,
  });

  const {
    currentView,
    isApplyingView,
    triggerUpdateCurrentView,
    onClickLayout,
    onViewsLoaded,
    createNewLayoutData,
    notifyViewMutation,
  } = useGanttViews({
    gantt,
    isGroupedRef,
    ganttFilterConditionsRef,
    applyGanttFilters,
    setSavedColumns,
    setIsGrouped,
    scheduleVersionId,
  });

  const handleGanttFilterChange = (filterConditionsObj: Record<string, FilterConditionEntry>) => {
    ganttFilterConditionsRef.current = filterConditionsObj;
    applyGanttFilters(filterConditionsObj);
    notifyViewMutation();
  };

  return (
    <>
      <div className="relative h-full w-full flex flex-col overflow-auto" ref={resizeRef}>
        {showShimmer && (
          <div className="bg-white z-[1] w-full h-full">
            <Shimmer />
          </div>
        )}
        {!showShimmer && _.get(props, "activeTab") != SIMULATION && (
          <div className=" w-full flex gap-5 overflow-auto justify-between ">
            <GridToolbar
              // `ganttToolbar`'s entries build `hidden`/`disabled` out of `contractId ||
              // scheduleVersionId` (both `string | undefined` on ToolbarProps), so both flags are
              // `string | boolean | undefined`; several entries also omit the `prefixIconTemplate`,
              // `cssClass` and `buttonType` that this `ToolbarItem` declares required. GridToolbar
              // reads both flags for truthiness alone and types them `boolean`, so the assertion is
              // hiding nothing but those unions — the fix is `Boolean(...)` on the expressions in
              // useToolbar.tsx, after which this passes unasserted, and against the toolbar's own
              // entry type rather than the scheduleDomain `ToolbarItem` imported here.
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- blocked on useToolbar.tsx building `hidden`/`disabled` as `string | boolean | undefined`
              toolbar={ganttToolbar as ToolbarItem[]}
              toolbarClickHandler={toolbarClickHandler}
              height={16}
              enableViews={true}
              viewsOptions={{
                headerName: "Views",
                name: "View",
                resourceType: GANTT_SCHEDULE_RESOURCE_TYPE,
                selectedLayoutKey: GANTT_SCHEDULE_SELECTED_LAYOUT_KEY,
                fetchParams: { projectId, scheduleId },
              }}
              onClickLayout={onClickLayout}
              onViewsLoaded={onViewsLoaded}
              createNewLayoutData={createNewLayoutData}
              triggerUpdateCurrentView={triggerUpdateCurrentView}
            />
            <div className="flex items-center h-full gap-1 font-bold text-sm me-4">
              <ButtonComponent id="fade-button" onClick={handleLayoutClick} buttonType={BUTTON_TYPES.TEXT_BUTTON}>
                <Text className="flex gap-2 items-center font-semibold text-sm text-primary_text">
                  {t("Layout")}
                  <DownArrow />
                </Text>
              </ButtonComponent>
              <Menu
                slotProps={{
                  paper: {
                    sx: {
                      border: "0.125rem solid #ECF3FE",
                      borderRadius: "0",
                    },
                  },
                  list: {
                    "aria-labelledby": "fade-button",
                    sx: {
                      py: 0,
                    },
                  },
                }}
                anchorEl={layoutAnchorEl}
                open={layoutOpen}
                onClose={handleLayoutClose}
                slots={{
                  transition: Fade,
                }}
              >
                <MenuItem
                  sx={{
                    borderBottom: "0.125rem solid #ECF3FE",
                    height: "3rem",
                    display: "flex",
                    alignItems: "center",
                    // width: "16.25rem",
                  }}
                  onChange={() => {
                    handleCriticalPath();
                    handleLayoutClose();
                  }}
                >
                  <FormControlLabel
                    control={<ToggleSwitchComponent checked={showCriticalPath} />}
                    label={t("Critical Path")}
                    sx={{
                      gap: "1rem",
                      width: "100%",
                    }}
                  />
                </MenuItem>
                <MenuItem
                  sx={{
                    // borderBottom: "2px solid #ECF3FE",
                    // width: "16.25rem",
                    height: "3rem",
                    display: "flex",
                    alignItems: "center",
                  }}
                  onChange={() => {
                    setShowResourceLoadDiagram(!showResourceLoadDiagram);
                    setCurrentSelectedTask({});
                    handleLayoutClose();
                  }}
                >
                  <FormControlLabel
                    control={<ToggleSwitchComponent checked={showResourceLoadDiagram} />}
                    label={t("Resource Load Diagram")}
                    sx={{
                      gap: "1rem",
                      width: "100%",
                    }}
                  />
                </MenuItem>
                {/* <MenuItem  sx={{
                    // borderBottom: "2px solid #ECF3FE",
                    height: 48,
                    display: "flex",
                    alignItems: "center",
                  }}>
                  <FormControlLabel control={<ToggleSwitchComponent disabled />} label="Resource Histogram"  sx={{
                    gap: 2,
                     width: "100%",
                  }}/>
                </MenuItem> */}
              </Menu>
              <FilterButton
                columns={ganttFilterColumns}
                data={ganttTasksDataset}
                onFilterChange={handleGanttFilterChange}
                getFieldValue={getGanttTaskFieldValue}
                initialFilterConditionsObject={currentView?.data?.filterConditions}
              />
              <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={handleColumnChooserClick}>
                <ColumnChooserIcon />
              </ButtonComponent>
              {!_.get(url, "fileId") && (
                <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={handleScreenClick}>
                  {showFullScreen ? <MinimiseIcon className="h-6 w-6" /> : <FullScreenIcon />}
                </ButtonComponent>
              )}
            </div>
          </div>
        )}
        <div id="ganttRoot" className="flex flex-1 w-full h-full overflow-auto" />
        {isApplyingView && !showShimmer && (
          <div className="fixed inset-0 z-[1600] flex items-center justify-center bg-white/60">
            <CircularProgress disableShrink />
          </div>
        )}
      </div>
      {showTaskModal && showTaskContractsModal()}
      {Array.isArray(_.get(attachedItems, "items")) && getTaskGridInContract()}
      {_.get(showWarning, "status") && warningModal()}
      {showBoqModal && linkBoqItemsModal()}
      {showProgressModal && (
        <UpdatesModal
          showProgressModal={showProgressModal}
          setShowProgressModal={setShowProgressModal}
          loadingProgressModal={loadingProgressModal}
          setLoadingProgressModal={setLoadingProgressModal}
          currentSelectedTask={currentSelectedTask}
          setCurrentSelectedTask={setCurrentSelectedTask}
          gantt={gantt}
          scheduleId={scheduleId!}
          ganttConfig={ganttConfig}
        />
      )}
      {showActivityInformationModal && (
        <ActivityInformationModal
          showActivityInformationModal={showActivityInformationModal}
          setShowActivityInformationModal={setShowActivityInformationModal}
          loadingActivity={loadingActivity}
          setLoadingActivity={setLoadingActivity}
          currentSelectedTask={currentSelectedTask}
          setCurrentSelectedTask={setCurrentSelectedTask}
          ganttConfig={ganttConfig}
          gantt={gantt}
          taskCounter={taskCounter}
          scheduleId={scheduleId!}
          fileVersionId={_.get(url, "fileId")}
          spaceId={spaceId}
          projectId={projectId}
        />
      )}
      {url.fileId && props.activeTab == SIMULATION && (
        <Simulation
          activeTab={props.activeTab!}
          ganttDataAvailable={ganttDataAvailable}
          viewType={props.viewType!}
          setDisableTabs={setDisableTabs!}
        />
      )}
      {updateModal()}
      {importModal()}
      {deleteActivityModal()}
      {showColumnChooserModal && (
        <Suspense>
          <ColumnChooser
            handleOpenColumnChooserModal={() => setShowColumnChooserModal(false)}
            handleSaveColumns={handleColumnChooserSaveClick}
            data={allColumns}
            selectedData={savedColumns}
          />
        </Suspense>
      )}
    </>
  );
});

GanttChart.displayName = "GanttChart";

export default GanttChart;
